/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from 'react';

export function useInfiniteScrollData(filteredTableData, chunkSize = 10) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const containerRef = useRef(null);

    const loadData = useCallback(() => {
        if (!Array.isArray(filteredTableData) || filteredTableData.length === 0) return;
        setLoading(true);
        try {
            const newData = filteredTableData.slice(page * chunkSize, (page + 1) * chunkSize);
            setData(prevData => [...prevData, ...newData]); // Always use latest data
            setHasMore(filteredTableData.length > (page + 1) * chunkSize);
            setPage(prev => prev + 1);
        } catch (error) {
            console.error('Error loading data:', error.message);
        } finally {
            setLoading(false);
        }
    }, [filteredTableData, page, chunkSize]);

    // Reset when filteredTableData length changes (filter applied) or completely replaced
    const dataLengthRef = useRef(filteredTableData.length);
    const prevFilteredDataRef = useRef(filteredTableData);
    
    useEffect(() => {
        // Only reset if the data length changed (filter applied/removed or new rows added)
        if (filteredTableData.length !== dataLengthRef.current) {
            setData([]);
            setPage(0);
            setHasMore(true);
            dataLengthRef.current = filteredTableData.length;
            prevFilteredDataRef.current = filteredTableData;
        } else if (filteredTableData !== prevFilteredDataRef.current) {
            // If different reference but same length, update loaded data
            setData(prevData => {
                // Only update if data is already loaded
                if (prevData.length === 0) return prevData;
                
                // Check if all current items exist in new data
                const allItemsExist = prevData.every(item => 
                    filteredTableData.some(d => d.id === item.id)
                );
                
                // If some items don't exist (rows deleted/replaced), reset
                if (!allItemsExist) {
                    setPage(0);
                    setHasMore(true);
                    dataLengthRef.current = filteredTableData.length;
                    prevFilteredDataRef.current = filteredTableData;
                    return [];
                }
                
                // Update existing items with latest data
                return prevData.map(item => {
                    const updated = filteredTableData.find(d => d.id === item.id);
                    return updated || item;
                });
            });
            prevFilteredDataRef.current = filteredTableData;
        }
    }, [filteredTableData]);

    // Initial load after reset
    useEffect(() => {
        if (page === 0 && filteredTableData.length > 0) {
            loadData();
        }
    }, [page, filteredTableData, loadData]);

    // Handle scrolling
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight * 0.8 && !loading && hasMore) {
                loadData();
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loadData, loading, hasMore]);

    return { data, setData, loading, hasMore, containerRef };
}
